// THE OFFICE HOST of §4.4's execution screen: a direct, idempotent POST.
//
// Spec 07 §9's offline clauses are all about the FLOOR — "everything queues
// (results, photos, completion) with client idempotency keys" is §4.4's floor
// bullet, and there is no §9 clause making an office desktop offline-capable.
// So the office writes go straight out, and a failure is reported INLINE, with
// its request id, in the office's own idiom — never parked in a device queue
// the person who made the write has no reason to open.
//
// EVIDENCE STILL GOES THROUGH THE REAL THREE-CALL FLOW. `POST /v1/files` →
// presigned PUT → `POST /v1/files/{id}/store`, under purpose `quality_evidence`
// — the same `createPhotoUploader` the floor drain runs, against a MEMORY photo
// store because there is nothing to survive here: an office write that fails is
// re-taken at the desk. Re-implementing the upload for the office would give the
// product two places where a photo's purpose, integrity hash and idempotency
// keys are decided, and D-096's whole design is that there is one.
//
// The port's `busy` is a real flag rather than a react-query mutation's, because
// one screen issues N result writes and one completion and the screen's own
// disabled-state has to cover all of them.

import { useCallback, useRef, useState } from "react";
import { client } from "../../../../lib/api";
import { newIdempotencyKey, unwrap } from "../../../../lib/request";
import { describeFailure, type OutboxSettlement } from "../../../floor-pwa/lib/floor-outbox";
import { resolveOutcome, type FloorOp } from "../../../floor-pwa/lib/floor-ops";
import { capturePhotos, createPhotoUploader } from "../../../floor-pwa/lib/photo-upload";
import { memoryPhotoStore } from "../../../floor-pwa/lib/photo-store";
import type { InspectionRunPort } from "./inspection-run-port";
import { useFailureActionArm } from "./use-failure-action-arm";

/** Upload evidence NOW and answer the stored file ids, in order. Throws the
 * shared `RequestError` on refusal, which the caller turns into the screen's
 * `needs_attention` with the server's own sentence. */
async function uploadEvidence(files: readonly File[]): Promise<string[]> {
  if (files.length === 0) return [];
  const store = memoryPhotoStore();
  const ids = await capturePhotos(store, files, { purpose: "quality_evidence" });
  const uploader = createPhotoUploader(store);
  const fileIds = await uploader.ensureUploaded(ids);
  await uploader.discard(ids);
  return fileIds;
}

/** Shared so the office port's identity is stable across renders. */
const EMPTY_SETTLEMENTS: ReadonlyMap<string, OutboxSettlement["outcome"]> = new Map();

export function useOfficeRunPort(): InspectionRunPort {
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  // §4.4's failure-action arm (07-B-3) — the same direct sender the floor
  // hands the screen; a disposition takes one path on both hosts.
  const failureActions = useFailureActionArm();

  const send = useCallback(async (op: FloorOp): Promise<OutboxSettlement> => {
    setBusy(true);
    try {
      const key = newIdempotencyKey();
      const response =
        op.kind === "quality_result"
          ? unwrap(
              await client.POST("/v1/quality/inspections/{id}/results", {
                params: { path: { id: op.inspectionId }, header: { "Idempotency-Key": key } },
                body: op.body,
              }),
              "quality-result",
            )
          : op.kind === "quality_complete"
            ? unwrap(
                await client.POST("/v1/quality/inspections/{id}/complete", {
                  params: { path: { id: op.inspectionId }, header: { "Idempotency-Key": key } },
                  body: op.body,
                }),
                "quality-complete",
              )
            : undefined;
      // THE SAME INTERPRETER THE DRAIN RUNS (D-080). A 200 is not yet an
      // application: `resolveOutcome` checks that the answer actually mentions
      // what was sent, and a 200 that does not becomes `conflicted` here
      // exactly as it would on a phone. Two code paths agreeing about HTTP and
      // disagreeing about what counts as success is how one shell ends up
      // showing a tick for a write the other shows as needing review.
      const resolution = resolveOutcome(op, key, response);
      if (resolution.kind === "conflicted") {
        return { outcome: "conflicted", reason: resolution.reason };
      }
      return { outcome: "applied", response };
    } catch (error) {
      return { outcome: "needs_attention", failure: describeFailure(error) };
    } finally {
      if (alive.current) setBusy(false);
    }
  }, []);

  const recordResult = useCallback<InspectionRunPort["recordResult"]>(
    async (input) => {
      let photoFileIds: string[];
      try {
        photoFileIds = await uploadEvidence(input.photos);
      } catch (error) {
        return { outcome: "needs_attention", failure: describeFailure(error) };
      }
      return send({
        kind: "quality_result",
        inspectionId: input.inspectionId,
        body:
          photoFileIds.length > 0 ? { ...input.body, photo_file_ids: photoFileIds } : input.body,
      });
    },
    [send],
  );

  const complete = useCallback<InspectionRunPort["complete"]>(
    async (input) =>
      send({
        kind: "quality_complete",
        inspectionId: input.inspectionId,
        body: input.body,
      }),
    [send],
  );

  // The office holds nothing: every write is awaited and its settlement is the
  // final one, so no card can be waiting on an answer that arrives later. An
  // empty map rather than an optional member — a host that cannot hold work
  // says so in the type, not by omission.
  return {
    recordResult,
    complete,
    busy,
    durableOffline: false,
    liveSettlements: EMPTY_SETTLEMENTS,
    failureActions,
  };
}
